import { ReactComponent as IconDownloadAll } from "@/assets/svgs/download-all-folder.svg";
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as IconClose } from '@/assets/svgs/icon-close.svg';
import { ReactComponent as IconTradeTrust } from '@/assets/svgs/tradetrust.svg';
import { ReactComponent as IconPrevious } from '@/assets/svgs/transactions-back-icon.svg';
import { ReactComponent as WarningIndicator } from '@/assets/svgs/verification-inidicator.svg';
import DataElementAttachmentCard from '@/components/AttachmentCard/index';
import Button from '@/components/Button';
import Portal from '@/components/Portal';
import SharedDataStatus from "@/components/SharedDataStatus/index";
import Toast from "@/components/Toast/index";
import ViewActivityLogModal from '@/components/ViewActivityLogModal/index';
import useConfig from '@/hooks/use-config';
import useEForm from "@/hooks/use-eform/index";
import useModal from '@/hooks/use-modal/index';
import usePage from '@/hooks/use-page';
import useToast from "@/hooks/use-toast/index";
import useTransaction from '@/hooks/use-transaction/index';
import { useLocation, useNavigate, useParams } from '@/lib/router-dom';
import DataElementVerificationInfo from "@/pages/eclip/components/data-element-verification-info.jsx";
import apiDownload from '@/services/api/download.js';
import API_FILES from '@/services/api/files.js';
import api from '@/services/api/messages.js';
import "@/translations/i18n";
import addFormats from "@/utils/custom-ajv-formats";
import rem from '@/utils/rem.js';
import Ajv from "ajv";
import classname from 'classnames';
import { orderBy } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from "react-redux";
import { AccessRestrictionCard } from "@/components/AccessRestrictionCard/index";
import FormRenderer from "@/components/EFormV2/EFormRenderer";
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import Modal from '@/componentsDS/Modal/Modal';
import { ReactComponent as IconConfigure } from '@/assets/svgs/icon-configure.svg';
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index";
import VerificationDetails, { VerificationStatus } from "@/componentsDS/VerificationDetails/VerificationDetails";
import { PrincipalsCard } from '@/components/PrincipalsCard';
import { useFeature } from '@/hooks/use-feature/index';
import { featureConstant } from '@/constants/configuration/feature';

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

const forwardingAllowedTypes = ["PULL", "RECEIVE"]

const Page = ({
	defaultFileFormat = "",
	defaultFileType = "",
	defaultIncludeAttachments = false,
	defaultIsValidForm = false,
	defaultEditEformPayload = true,
	defaultAttachmentLists = []
}) => {

	/*=================================================1
		General
	/================================================*/
	const { currentState } = useLocation();

	const { breadcrumbsItems } = usePage({
		title: "Transaction",
		breadcrumbs: [
			{
				label: '',
				link: '/',
			},
			{
				label: currentState?.type === "consumer" ? "Received Data" : "Shared Data",
				link: currentState?.type === "consumer" ? "/received-data" : "/shared-data",
			},
			{
				label: 'Transaction',
				link: ``,
			}
		]
	})

	const { goTo } = useNavigate();

	const { messageId } = useParams()

	const { profile } = useSelector(state => state.auth)

	const [attachmentList, setAttachmentList] = useState(defaultAttachmentLists)
	const [hasAttachmentsInSchema, setHasAttachmentsInSchema] = useState(false);

	const { toastList, addToastMessage } = useToast();

	const verificationStatusType = {
		notApplicable: "NOT APPLICABLE",
		verified: "VERIFIED",
		unverified: "UNVERIFIED"
	}

	/*=================================================
		Error Modal
	/================================================*/
	const [error, setError] = useState()
	function handleError() {
		setError()
	}

	/*=================================================
		API
	/================================================*/

	const { config, provideItems, systems } = useConfig()
	const { currentItem, fetchOne, getActivityLog, activityLog, packageErrorMsg } = useTransaction()
	const { error: dataExpiryError } = useSelector(state => state.dataExpiry)
	const [loading, setLoading] = useState(false)

	const [storedFor, setStoredFor] = useState(null)
	const [participants, setParticipants] = useState();
	const [showActivityLogOption, setShowActivityLogOption] = useState(true);

	function checkHasAttachmentsValues(payload) {
		let attachments = [];
		for (let key in payload) {
			if (key === 'attachments' && payload[key]) {
				attachments.push(...payload[key]);
			} else if (typeof payload[key] === 'object' && payload[key] !== null) {
				attachments.push(...checkHasAttachmentsValues(payload[key]));
			}
		}
		return attachments;
	}

	function hasAttachmentPropInSchema(schema) {
		if (schema?.type?.includes('object') && schema.properties) {
			for (const [key, value] of Object.entries(schema.properties)) {
				if (key === 'attachments' || (value?.type?.includes('object') && hasAttachmentPropInSchema(value))) {
					return true;
				}
			}
		}
		return false;
	}

	useEffect(() => {
		fetchOne(messageId)
		getActivityLog(messageId)
	}, [config])

	useEffect(() => {
		// checking attachments prop on payload object
		const attachmentListIsPresent = checkHasAttachmentsValues(currentItem?.payload)
		const attachmentLists = orderBy(attachmentListIsPresent.map((_item, index) => ({ ...attachmentListIsPresent[index], id: index, $selected: false })), [option => option.filename.toLowerCase()], ["asc"]);
		setAttachmentList(attachmentLists)

		// checking attachments prop on schema object
		if (currentItem?.dataElement?.elementSchema) {
			setHasAttachmentsInSchema(hasAttachmentPropInSchema(currentItem.dataElement.elementSchema));
		}

		const storedFor = currentItem ? currentItem.storedFor : null;
		setStoredFor(storedFor);

		const provideDataElement = provideItems?.filter(dataElement => dataElement.id === currentItem.dataElement.id)[0]
		const provideParticipants = provideDataElement?.to?.map(element => element)
		setParticipants(provideParticipants)
		setShowActivityLogOption(!currentItem?.type === "RECEIVE" || activityLog?.length > 0)
	}, [currentItem])

	useEffect(() => {
		if (packageErrorMsg) {
			addToastMessage("Failed to get activity log", "error")
		}
	}, [packageErrorMsg])

	/*=================================================
		Modal
	/================================================*/

	const { visible: visibleDownloadData, show: showDownloadData, hide: hideDownloadData } = useModal()
	const { visible: visibleViewRequestLog, hide: hideViewRequestLog, show: showViewRequestLog } = useModal();
	const { visible: visibleRequestResubmit, hide: hideRequestResubmit, show: showRequestResubmit } = useModal();

	/*=================================================
		Options
	/================================================*/
	const [fileType, setFileType] = useState(defaultFileType)
	const [fileFormat, setFileFormat] = useState(defaultFileFormat)
	const [fileSize, setFileSize] = useState(0)
	const [totalFiles, setTotalFiles] = useState(0)
	const [includeAttachments, setIncludeAttachments] = useState(defaultIncludeAttachments)

	/*=================================================
		Routes
	/================================================*/

	function forward() {
		goTo(`/received-data/transaction/${messageId}/forward`)
	}

	function goBack() {
		if (currentState.type === "consumer") {
			return goTo('/received-data')
		}

		return goTo('/shared-data')
	}

	function changeFileType(value) {
		if ((fileType === 'zip' && value !== 'zip') || value === 'tradetrust') {
			setFileFormat()
			setIncludeAttachments()
		}
		setFileType(value);
	}

	/*=================================================
		Function
	/================================================*/

	async function checkFileSize() {
		const [error, data] = await api.checkFileSize({
			id: messageId
		})

		if (error) {
			return setError(error?.error?.message || "Failed to check file size")
		}

		setFileSize(data.totalFileSize)
		setTotalFiles(data.totalFiles)
	}

	async function downloadPayload() {
		setLoading(true)
		const [error, result] = await api.downloadFile({ id: messageId }, "csv", {
			params: {
				"includeAttachments": false
			}
		})
		setLoading(false)
		if (error) {
			return setError(error?.error?.message || "Failed to download file(s)")
		}
		const link = document.createElement("a");
		const href = `data:text/csv;charset=utf-8,%EF%BB%BF${encodeURIComponent(result)}`
		link.href = href;
		link.download = `${currentItem?.dataElement.id + ".csv"}`;
		link.click();
		return
	}

	async function downloadFile() {
		const [error, result] = await api.downloadFile({ id: messageId }, fileFormat ?? fileType, {
			params: {
				"includeAttachments": includeAttachments
			}
		})

		if (error) {
			return setError(error?.error?.message || "Failed to download file(s)")
		}
		hideDownloadData()

		const link = document.createElement("a");
		if (includeAttachments === "false") {
			if (fileFormat === "json") {
				const href = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(result, null, 2))}`
				link.href = href;
				link.download = `${currentItem?.dataElement.id + ".json"}`;
				link.click();
				setFileFormat("")
				return
			}
			if (fileFormat === "csv") {
				const path = `data:text/csv;charset=utf-8,%EF%BB%BF${encodeURIComponent(result)}`
				link.href = path;
				link.download = `${currentItem?.dataElement.id + ".csv"}`;
				link.click();
				setFileFormat("")
				return
			}
		}

		if (fileType === 'tradetrust') {
			const item = attachmentList.find(x => x.isGeneratedTT === true);
			const [error, result] = await apiDownload.download(item.file_content)

			if (error) {
				return setError(error?.error?.message || "Unable to download file(s)")
			}

			const data = `data:application/pdf;base64,${result}`
			const link = document.createElement("a")
			link.href = data
			link.download = `${currentItem?.dataElement.id + ".tt"}`;
			link.click()
			setFileFormat("")
			return
		}

		const path = `data:application/zip;base64,${result}`
		link.href = path;
		link.download = "attachment.zip";
		link.click();
		setFileFormat("")
	}

	const getTransactionHeadingClass = classname({
		"title-header transactions-eform-heading__bottom": true,
		"transaction-heading--disable": currentItem?.status.toUpperCase() === "EXPIRED"
	})

	let foundSystem;

	if (currentItem?.type !== 'STORE') {
		if (systems.filter(x => x.systems && x.systems.length > 0 && x.name !== 'My Pitstop').length > 0) {
			foundSystem = systems.map(org => org.systems).flat().find(x => x.id === currentItem?.sysId)
		}
		else {
			foundSystem = systems.map(org => org.system).find(x => x.id === currentItem?.sysId)
		}
	}

	const foundOrg = currentItem?.type === 'STORE' ? undefined : systems.find(system => system.id === currentItem?.orgId)
	const foundOnBehalfOf = currentItem?.on_behalf_of_id && systems.find(org => org.id === currentItem?.on_behalf_of_id) ? systems.find(org => org.id === currentItem?.on_behalf_of_id) : undefined;
	const onBehalfOfName = foundOnBehalfOf?.name

	// Check if other-info section should be visible: either has attachments OR has principals (with feature flag enabled)
	const hasPrincipalsToShow = useFeature(featureConstant.AGENT_PRINCIPAL_UI) && currentItem?.principals && currentItem.principals.length > 0;
	const shouldShowOtherInfo = hasAttachmentsInSchema || hasPrincipalsToShow;

	// Determine principals info based on transaction type and principal type
	// "for" = my principals, "to" = recipient's principals (PUSH), "from" = provider's principals (PULL/RECEIVE)
	const principalType = currentItem?.principals?.[0]?.type;
	const isSharedData = currentItem?.type === "PUSH" || currentItem?.type === "STORE" || currentItem?.type === "PROVIDE";
	const isReceivedData = currentItem?.type === "RECEIVE" || currentItem?.type === "PULL";
	const isMyPrincipal = (isReceivedData && principalType === "CONSUME_ON_BEHALF_OF_PRINCIPAL") ||
		(isSharedData && principalType === "PROVIDE_ON_BEHALF_OF_PRINCIPAL");

	const principalsTitle = isMyPrincipal ? "My Principals" : (isReceivedData ? "Provider's Principals" : "Recipient's Principals");
	const principalsPreposition = isMyPrincipal ? "for" : (isReceivedData ? "from" : "to");
	const orgPreposition = isSharedData ? "to" : "from";

	// Reusable elements
	const principalsLink = (
		<button
			type="button"
			tabIndex={0}
			className="transaction-title-header__principals-link"
			onClick={() => document.getElementById('principals-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
		>{currentItem?.principals?.length} Principal{currentItem?.principals?.length > 1 ? 's' : ''}</button>
	);

	const onBehalfOfElement = onBehalfOfName && (
		<span className="data-table__table__body__content__proxy">
			&ensp; on behalf of {onBehalfOfName}
		</span>
	);

	const viaSystemElement = foundSystem && (
		<span className="data-table__table__body__content__proxy">
			&ensp; via {foundSystem?.name}
		</span>
	);

	const transactionInfoWithPrincipals = (
		<>
			{" "}{principalsPreposition} {principalsLink} {principalsPreposition === orgPreposition ? "of" : orgPreposition} {foundOrg?.name}
			{onBehalfOfElement}
			{viaSystemElement}
		</>
	);

	/*=================================================1
		Eform in Edit State
	/================================================*/
	const [editEformPayload, setEditEformPayload] = useState(defaultEditEformPayload);

	const {
		payload,
		setPayload,
		getFullJson,
		extractCombinedSchema,
		getSchemaWithoutAttachment
	} = useEForm();

	const [schema, setSchema] = useState(null);

	useEffect(() => {
		const elementSchema = currentItem?.dataElement?.elementSchema
		let payload = currentItem?.payload

		if (Array.isArray(payload)) {
			payload = payload[0];
		}

		const plainSchema = extractCombinedSchema(
			elementSchema,
			payload
		);
		const schemaWithoutAttachment = getSchemaWithoutAttachment(plainSchema);

		setSchema(schemaWithoutAttachment);
		setPayload(getFullJson(schemaWithoutAttachment, payload,plainSchema));
	}, [currentItem?.dataElement, editEformPayload]);

	function editEformDEDetails() {
		setAttachmentList(currentItem?.attachments)
		setEditEformPayload(!editEformPayload)
	}

	/*=================================================
		Submit Function
	/================================================*/
	const [isValidForm, setIsValidForm] = useState(defaultIsValidForm);
	const [isValidFormat, setIsValidFormat] = useState(true);

	useEffect(() => {
		if (payload) {
			const validate = ajv.compile(schema);
			const { attachments, ...payloadToBeValidate } = payload;
			const isPayloadValid = validate(payloadToBeValidate);
			setIsValidForm(isValidFormat && isPayloadValid);
			return isPayloadValid;
		}
	}, [payload]);

	const reformatAttachments = (attachments) => attachments.map(({ filename, file_content }) => ({ filename, file_content }));

	async function submitPayload() {
		setLoading(true);

		let updatedAttachments = [];
		const attachmentsWithNoFileContent = attachmentList?.filter((_item) => !_item.file_content);
		const attachmentsWithFileContent = reformatAttachments(attachmentList?.filter((_item) => _item.file_content));
		if (attachmentsWithNoFileContent.length > 0) {
			const [error, uploadFilesResult] = await API_FILES.upload(attachmentsWithNoFileContent);

			if (error) {
				setLoading(false)
				return setError(error?.error?.message || error?.message || "Unable to upload the files");
			}
			updatedAttachments = reformatAttachments(uploadFilesResult?.reduce((pre, cur) => [...pre, ...cur.data.data], []));
		}

		const body = {
			"payload": { ...payload, attachments: [...attachmentsWithFileContent, ...updatedAttachments] }
		}

		const [error] = await api.updateTransactionMessages(messageId, body)

		setLoading(false)

		if (error) {
			addToastMessage(error?.error?.message || "Unable to update the Request", "error")
			hideRequestResubmit()
			return setError(error?.error?.message || "Unable to update the Request")
		}

		setEditEformPayload(!editEformPayload)
		addToastMessage("You have successfully updated the request.", "success")
		fetchOne(messageId)
		hideRequestResubmit()
		getActivityLog(messageId)
	}

	const statusLookup = {
		'PENDING_APPROVAL': { text: "Created", date: currentItem?.created },
		'REJECTED': { text: "Created", date: currentItem?.created },
		'EXPIRED': { text: "Shared", date: currentItem?.modified },
		'FAIL': { text: "Shared", date: currentItem?.modified },
		'PENDING': { text: "Shared", date: currentItem?.modified },
		'SUCCESS': { text: "Shared", date: currentItem?.modified },
		'APPROVED': { text: "Shared", date: currentItem?.modified }
	};

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<div className={getTransactionHeadingClass}>
				<div className="transaction-title-header__left">
					<span className="transaction-title-header__title__status--row">
						<div className='transaction-title-header__heading'>
							{"Transaction"}
						</div>

						<div className='title-header__mc_status'>
							<SharedDataStatus
								status={currentItem?.status} isReceivedData={['RECEIVE', 'PULL'].includes(currentItem?.type)}
							/>
						</div>

						<div className='title-header__mc_status'>
							{currentItem?.receivingType === "COPY" && (
								<SharedDataStatus
									status={currentItem?.providerTransactionStatus === "FAIL" ? "COPY_FAILED" : "COPY_SUCCESS"}
									isReceivedData={['RECEIVE', 'PULL'].includes(currentItem?.type)}
								/>
							)}
						</div>

					</span>
				</div>
				<div className="transaction-title-header__right">
					{currentItem?.status.toLowerCase() !== "rejected" &&
						<>
							{
								forwardingAllowedTypes.includes(currentItem?.type) && <>
									<Button round ghost maxWidth={140} text={"Forward"}
										id="transactions-forward-button-id"
										submit={() => { forward() }}
										disabled={currentItem?.status.toUpperCase() === "EXPIRED"}
									/> &nbsp;
								</>
							}
							<Button round
								full maxWidth={140} text={"Download All"}
								submit={() => {
									checkFileSize()
									showDownloadData()
									setFileType("zip")
									setIncludeAttachments("true")
									setFileFormat("")
								}}
								disabled={currentItem?.status.toUpperCase() === "EXPIRED"}
							/>
						</>
					}
					{currentItem?.status.toLowerCase() === "rejected" && <>
						{editEformPayload && <>
							<Button round
								full ghost maxWidth={140} text={"Download All"}
								submit={() => {
									checkFileSize()
									showDownloadData()
									setFileType("zip")
									setIncludeAttachments("true")
									setFileFormat("")
								}}
								disabled={currentItem?.status.toUpperCase() === "EXPIRED"}
							/> &nbsp;
							{profile?.makerCheckerRole === "MAKER" && <Button round
								full maxWidth={140} data-testid="eform-edit-request-button" text={"Edit Request"} submit={() => editEformDEDetails()}
							/>}
						</>}
						{!editEformPayload && <>
							<Button round
								full ghost maxWidth={140} text={"Cancel"} submit={() => editEformDEDetails()}
							/> &nbsp;
							<Button round
								full maxWidth={140} text={"Resubmit"}
								disabled={!isValidForm}
								data-testid="eform-resubmit-button"
								submit={() => showRequestResubmit()}
							/>
						</>}
					</>}
				</div>
			</div>

			<div className="title-header__title__meta__cols">
				{
					(currentItem?.referenceId && currentItem?.referenceId !== "" && currentItem?.referenceId !== null) && (
						<div className="transaction-title-header__title__meta__col">
							Reference ID {currentItem?.referenceId}
						</div>
					)
				}
			</div>
			<div className="title-header__title__meta__cols">
				<div className="transaction-title-header__title__meta__col">
					{currentItem?.dataElement?.name} {hasPrincipalsToShow ? (
						transactionInfoWithPrincipals
					) : (
						<>
							{foundOrg && (
								<>
									{orgPreposition} {foundOrg?.name}
									{onBehalfOfElement}
								</>
							)}
							{viaSystemElement}
						</>
					)}
				</div>
			</div>
			<div className="title-header__title__meta__cols">
				<div className="transaction-title-header__title__meta__col-date">
					{
						(currentItem?.type === "PUSH" || currentItem?.type === "STORE" || currentItem?.type === "PROVIDE") && (
							<>
								{statusLookup[currentItem?.status.toUpperCase()]?.text} &nbsp;
								{moment(statusLookup[currentItem?.status.toUpperCase()]?.date || null).format('DD/MM/YYYY h:mm A')}
							</>
						)
					}
					{
						(currentItem?.type === "PULL" || currentItem?.type === "RECEIVE") && currentItem?.status !== "Pending" && (
							<>
								Received &nbsp;
								{moment(currentItem?.created).format('DD/MM/YYYY h:mm A')}
							</>
						)
					}
					{
						(currentItem?.isTTFlow || (currentItem?.verificationStatus && currentItem?.verificationStatus !== 'NOT APPLICABLE')) && (
							<span className='transaction-title-header__title__meta__col__tradetrust-icon'>
								<IconTradeTrust /> <span>TradeTrust Enabled</span>
							</span>
						)
					}
				</div>
			</div>

			{profile?.organization?.makerChecker && showActivityLogOption && (
				<div className="title-header__title__meta__cols">
					<div className='transaction-title-header__title__meta__col title-header__title__view-request-log' data-testid="view-request-log" onClick={() => showViewRequestLog()}>
						View Activity Log
					</div>
				</div>
			)}

			{currentItem?.payload !== null && (
				<div className="transaction-update transactions-data-element-payload-data-row__details">
					{currentItem?.payload && <div className="data-element-payload-data-row__eform data-element-payload-data-row__section">
						<div className="transactions-data-element-payload-data-heading-actions">
							{editEformPayload && <div onClick={() => downloadPayload()} data-testid="transaction-download-icon" className='transactions-data-element-payload-action-icons tooltip'>
								<div className="attachment-card__btn-download-all">
									<div className="attachment-card__btn-icon DS__icon--theme"><IconDownloadAll /></div>
									<div className="attachment-card__btn-text">Download</div>
								</div>
								<span className="tooltiptext">Download as CSV</span>
							</div>}
						</div>
						<div className="transactions-data-element-payload-heading-details">
							<div className="transactions-data-element-payload-heading-details-left">{currentItem?.dataElement?.name}</div>
							<div className="transactions-data-element-payload-heading-details-right">*Refers to mandatory fields</div>
						</div>
						<div className="transaction__eform-header-border"></div>
						<FormRenderer
							schema={schema}
							payload={payload}
							isReadOnly={editEformPayload}
							setPayload={setPayload}
							setIsValidFormat={setIsValidFormat}
						/>

					</div>}
					<div className="data-element-payload-data-row__other-info" data-testid="attachment-card" hidden={!shouldShowOtherInfo}>
						{/* Do not change order of components */}
						{currentItem?.payload && currentItem?.verificationStatus !== verificationStatusType.notApplicable &&
							<>
								<div className="transactions-data-element-payload__verification-detail">
									<VerificationDetails
										status={currentItem?.verificationStatus === verificationStatusType.verified ? VerificationStatus.SUCCESS : VerificationStatus.FAILURE}
										issuer={currentItem?.issuer}
										learnMore={ () => {
											window.open('https://docs.tradetrust.io/docs/introduction/what-is-tradetrust', '_blank', 'noopener,noreferrer');
										}}
									/>
								</div>
								<br />
							</>
						}
						{currentItem?.payload && <DataElementAttachmentCard
							stateType={editEformPayload ? "view" : "edit"}
							makerChecker={true}
							dataElement={currentItem?.dataElement?.name}
							attachmentList={attachmentList}
							setAttachmentList={setAttachmentList}
						/>}
						{
							["STORE", "PULL", "RECEIVE"].includes(currentItem?.type) && currentItem?.status?.toLowerCase() !== "expired" &&
							<AccessRestrictionCard storedFor={storedFor} type={currentItem?.type} participants={participants} setError={setError} currentItem={currentItem} fetchOne={fetchOne} />
						}
						{hasPrincipalsToShow && (
							<div id="principals-card">
								<PrincipalsCard
									principals={currentItem.principals}
									title={principalsTitle}
								/>
							</div>
						)}
					</div>
				</div>
			)}

			<Modal visible={visibleDownloadData} variant="medium" close={() => {
				hideDownloadData()
				setFileType()
				setIncludeAttachments()
			}}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon'}>
											<IconConfigure />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Download Transaction
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="download-transaction__rows">
								<div className="download-transaction__row">
									You are about to export Transaction {messageId} ({fileSize}MB)
								</div>
								<div className="download-transaction__row">
									<div className="download-transaction__row__left">
										Export data as
									</div>
									<div className="download-transaction__row__right" style={{ width: "70%" }}>
										<div className='download-transaction__row__left-right-flex'>
											<div className="download-transaction__row__right__radio" style={{ width: '30%' }}>
												<input type="radio" value="zip" name="fileType" id="zip"
													onClick={(e) => changeFileType(e.target.value)}
													checked={fileType === 'zip' && includeAttachments === "true"}
													onChange={() => {
														return setIncludeAttachments(includeAttachments === "true" ? "false" : "true")
													}}
												/> <label htmlFor="zip">ZIP file</label>
											</div>
											{
												currentItem?.isTTFlow && (
													<div className="download-transaction__row__right__radio">
														<input type="radio" value="tradetrust" name="fileType" id="tradetrust"
															onClick={(e) => changeFileType(e.target.value)}
															checked={fileType === 'tradetrust'}
														/> <label htmlFor="tradetrust">TradeTrust file</label>
													</div>
												)
											}
										</div>

										{
											fileType !== 'tradetrust' && (
												<div className="download-transaction__row__right__radio" style={{ marginTop: '20px' }}>
													<input type="radio" value="json" name="fileFormat" id="json"
														onClick={(e) => setFileFormat(e.target.value)}
													/> <label htmlFor="json">JSON</label>
												</div>
											)
										}
										{
											fileType !== 'tradetrust' && (
												<div className="download-transaction__row__right__radio">
													<input type="radio" value="csv" name="fileFormat" id="csv"
														onClick={(e) => setFileFormat(e.target.value)}
													/> <label htmlFor="csv">CSV</label>
												</div>
											)
										}

									</div>
								</div>
								{
									fileType !== 'tradetrust' && (
										<div className="download-transaction__row">
											<div className="download-transaction__row__left">
												Include attachments
											</div>
											<div className="download-transaction__row__right" style={{ width: "70%" }}>
												<div className='download-transaction__row__left-right-flex'>
													<div className="download-transaction__row__right__radio" style={{ width: '30%' }}>
														<input type="radio" value={"true"} name="includeAttachment" id="yes"
															checked={includeAttachments === "true"}
															onClick={(e) => setIncludeAttachments(e.target.value)}
															disabled={fileSize > 250 || totalFiles === 0}
														/> <label htmlFor="yes">Yes</label>
														{
															(fileSize > 250) && (
																<span className="download-transaction__row__right__radio__message">
																	Download size is too large
																</span>
															)
														}
													</div>
													<div className="download-transaction__row__right__radio">
														<input type="radio" value={"false"} name="includeAttachment" id="no"
															checked={includeAttachments === "false"}
															onClick={(e) => setIncludeAttachments(e.target.value)}
															disabled={fileType === 'tradetrust'}
														/> <label htmlFor="no">No</label>
														<span className="download-transaction__row__right__radio__message">
															&nbsp; (Download data only)
														</span>
													</div>
												</div>
											</div>
										</div>
									)
								}

							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button text="Cancel"
								ghost
								submit={() => {
									hideDownloadData()
									setFileType()
									setIncludeAttachments()
								}}
							/>
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button
								data-testid="download-transaction-confirm-btn"
								text="Confirm"
								disabled={(!fileFormat || !includeAttachments) && (fileType !== 'tradetrust')}
								submit={() => {
									downloadFile()
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			{/* <DownloadAllDataModal
				visible={visibleDownloadData}
				hide={hideDownloadData}
				fileSize={fileSize}
				totalFiles={totalFiles}
				containTTFile={currentItem?.isTTFlow}
				downloadFile={downloadFile}
			/> */}

			{
				loading && (
					ReactDOM.createPortal(
						<div className='modal white'>
							<div className='loading'>
								<img src={assetUrl("/loading.gif")} alt="loader" />
							</div>
						</div>
						, document.body
					)
				)
			}

			<ViewActivityLogModal
				visible={visibleViewRequestLog}
				close={hideViewRequestLog}
				schema={schema}
				activityLog={activityLog}
			/>

			{/* Request Resubmit Modal */}
			<Modal dataTestId="request-resubmit-modal" visible={visibleRequestResubmit} variant="medium" close={hideRequestResubmit} >
				<CardConfigure>
					<CardConfigure.Header >
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<span className="req-resubmit-modal__icon">
											<WarningIndicator />
										</span>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Resubmit Request
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							Are you sure you want to resubmit the request ?
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Left>
							<Button
								text="Cancel"
								data-testid="request-resubmit-modal-cancel-btn"
								ghost
								noborder
								customeHover
								round
								submit={() => hideRequestResubmit()}
							/>
						</CardConfigure.Footer.Left>
						<CardConfigure.Footer.Right>
							<Button
								data-testid="request-resubmit-modal-confirm-btn"
								text="Confirm"
								submit={() => submitPayload()}
								full
								round
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>

				</CardConfigure>

			</Modal >

			<Toast autoDelete={true} toastList={toastList} />
		</>
	)
}

export default Page;

Page.propTypes = {
	defaultFileFormat: PropTypes.string,
	defaultFileType: PropTypes.string,
	defaultIncludeAttachments: PropTypes.bool,
	defaultIsValidForm: PropTypes.bool,
	defaultEditEformPayload: PropTypes.bool,
	defaultAttachmentLists: PropTypes.array
}
